import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';
import { prepararParaValidacao, validarComSchema } from './validacaoYup';

/* Erros do react-hook-form ({ message, type, ref }) reduzidos à mensagem, mantendo o aninhamento. */
const paraMensagens = (no) => {
    if (!no || typeof no !== 'object') return undefined;
    if (typeof no.message === 'string' && no.type !== undefined) return no.message;
    if (Array.isArray(no)) return no.map(paraMensagens);

    const saida = {};
    Object.keys(no).forEach(chave => {
        if (chave === 'ref') return;
        const valor = paraMensagens(no[chave]);
        if (valor !== undefined) saida[chave] = valor;
    });
    return Object.keys(saida).length ? saida : undefined;
};

/* Todos os campos dos valores marcados como tocados — depois de submeter, todo erro aparece. */
const marcarTodos = (valor) => {
    if (Array.isArray(valor)) return valor.map(marcarTodos);
    if (valor && typeof valor === 'object' && !(valor instanceof Date) && !(valor instanceof Blob)) {
        const saida = {};
        Object.keys(valor).forEach(chave => { saida[chave] = marcarTodos(valor[chave]); });
        return saida;
    }
    return true;
};

/*
 * Formulário das etapas e diálogos da inscrição sobre o react-hook-form
 * (useForm + yupResolver). Os campos são controlados — valor vindo de
 * `valores` (watch) e alteração por `aoMudar`/`definirValor` —, e a validação
 * segue o comportamento da tela: o erro de um campo aparece quando ele perde o
 * foco, e depois da primeira tentativa de avançar/salvar todos aparecem e se
 * atualizam a cada alteração.
 *
 * `api` é o que a etapa expõe por ref ao FormInscricao: ler os valores,
 * validar a etapa inteira e exibir os erros na tela.
 */
export default function useFormularioInscricao({ valoresIniciais, schema, reinicializar = false, aoSubmeter }) {
    const schemaRef = useRef(schema);
    schemaRef.current = schema;
    const aoSubmeterRef = useRef(aoSubmeter);
    aoSubmeterRef.current = aoSubmeter;

    const form = useForm({
        defaultValues: valoresIniciais,
        mode: 'onSubmit',
        /*
         * O schema pode depender de props (configuração do formulário); sempre o
         * atual. Valida a cópia preparada ('' = não informado) e, quando passa,
         * entrega os valores como foram digitados — sem a conversão do Yup.
         */
        resolver: async (valores, contexto, opcoes) => {
            const resultado = await yupResolver(schemaRef.current, {}, { rawValues: true })(
                prepararParaValidacao(valores), contexto, opcoes
            );
            return Object.keys(resultado.errors).length ? resultado : { values: valores, errors: {} };
        }
    });
    const { watch, setValue, trigger, reset, getValues, handleSubmit, formState } = form;
    const { errors, submitCount, isSubmitting } = formState;
    const valores = watch();
    const [tocadosCampos, setTocadosCampos] = useState({});

    /* Os dados da inscrição mudaram lá em cima (outra etapa salvou): recomeça com eles. */
    const iniciaisRef = useRef(valoresIniciais);
    useEffect(() => {
        if (!reinicializar || isEqual(iniciaisRef.current, valoresIniciais)) return;
        iniciaisRef.current = valoresIniciais;
        reset(valoresIniciais);
        setTocadosCampos({});
    });

    const revalidar = (nome) => {
        if (submitCount > 0) {
            trigger();
        } else if (get(tocadosCampos, nome) || get(errors, nome)) {
            trigger(nome);
        }
    };

    const definirValor = (nome, valor) => {
        setValue(nome, valor, { shouldDirty: true });
        revalidar(nome);
    };

    const aoMudar = (evento) => {
        const { name, type, checked, value } = evento.target;
        if (!name) return;
        const valor = type === 'checkbox'
            ? checked
            : (type === 'number' && value !== '' ? parseFloat(value) : value);
        definirValor(name, valor);
    };

    const aoSair = (evento) => {
        const nome = evento?.target?.name;
        if (!nome) return;
        setTocadosCampos(atual => {
            const novo = cloneDeep(atual);
            set(novo, nome, true);
            return novo;
        });
        trigger(nome);
    };

    const submeter = (evento) => handleSubmit(dados => aoSubmeterRef.current && aoSubmeterRef.current(dados))(evento);

    const reiniciar = ({ values } = {}) => {
        reset(values !== undefined ? values : valoresIniciais);
        setTocadosCampos({});
    };

    const erros = paraMensagens(errors) || {};

    return {
        valores,
        erros,
        tocados: submitCount > 0 ? marcarTodos(valores) : tocadosCampos,
        submitCount,
        enviando: isSubmitting,
        valido: Object.keys(errors).length === 0,
        aoMudar,
        aoSair,
        definirValor,
        submeter,
        reiniciar,
        api: {
            getValores: () => getValues(),
            validar: () => validarComSchema(schemaRef.current, getValues()),
            exibirErros: () => handleSubmit(() => {})()
        }
    };
}
